// MA-632 — single source of truth for skill→pack routing.
//
// Which content pack a skill's items live in used to be computed in THREE
// places (two consulting a 12-entry PACK_OVERRIDE table that only agreed
// because someone remembered to keep them in sync; a third — buildDecisionInput
// .ts's readContentSafety — carried no override at all and silently asked the
// wrong file whether a child has safe content). This module is the extraction:
// the ONE place PACK_OVERRIDE and primaryPackForSkill live. Every other file
// that needs skill→pack routing imports from here. See
// docs/build/specs/MA-632-pack-routing-single-source.md for the full history
// and the audit guard (scripts/audit/packRoutingSingleSourceGuard.mjs) that
// keeps a fourth copy from growing back.
//
// Pure (no React, no browser globals) — safe to import from `tsx`-run audit
// scripts and Node test files as well as the React hook that originally owned
// this table.

import type { ContentPackName } from "@/curriculum/types/content";
import { domainForSkill } from "./skillMeta";

// Explicit skill→pack overrides that must NOT go through domainForSkill.
// Foundation letter skills (names ms.read.01, formation ms.read.03) are
// readingDecodingMastery → phonics_decoding under domainForSkill, which
// would wrongly route them to controlledSentences. They live in the
// foundationItems pack. (MA-671 slice 1b, 2026-09-19: ms.read.02 letter
// sounds is ROUTED now — the 30 ms.read.02 items were re-framed on their
// keyword and narrated in slice 1a, closing the "no audio yet" blocker this
// comment used to name; see FOUNDATION_SKILL_IDS in skillMeta.ts for the
// matching FoundationSession-delivery entry.)
export const PACK_OVERRIDE: Record<string, ContentPackName> = {
  "ms.read.01": "foundationItems",
  "ms.read.02": "foundationItems",
  "ms.read.03": "foundationItems",
  // S5.5 (founder-locked 2026-06-10): HFW routes to its hfwSubset items in
  // encodingWords — the skill served ZERO before (its high_frequency_words
  // domain fell to controlledSentences, which has no ms.read.08 items). Only
  // the renderable HFW modalities (multiple_choice / sentence_completion)
  // are launch_safe; trace + listen_choose are integration-gated. ms.read.09
  // deliberately has NO override — it keeps its 175-item controlledSentences
  // cloze pool (the encodingWords sound-box items have no renderer until T2-D).
  "ms.read.08": "encodingWords",
  // T2-D: CVC encoding sound-box items live in encodingWords; without this
  // ms.read.07 falls to controlledSentences (no ms.read.07 items). Served
  // SCAFFOLDED via FoundationSession.
  "ms.read.07": "encodingWords",
  // MA-126: ms.read.04 (ch1 supportSkill, chapters.json) is the SAME
  // shape as ms.read.07 — 150 authored sound-box items, no renderer at
  // controlledSentences (0 items there). Without this override it fell
  // to controlledSentences and served ZERO. Served SCAFFOLDED via
  // FoundationSession (see FOUNDATION_SKILL_IDS in skillMeta.ts).
  "ms.read.04": "encodingWords",
  // MA-671 slice 2b (2026-09-21): ms.read.05 (Oral segmenting) is ROUTED
  // now — but to `foundationItems`, NOT `encodingWords`. ADR-055's lock was
  // always specifically on the drag-letters `sound_box_encode` modality (the
  // 150 pre-existing ms.read.05 records, which live in `encodingWords.json`
  // and stay `exclude_independent_practice` there, untouched and unrouted —
  // this override never points at that pack, so they remain structurally
  // unreachable). The 36 NEW `phonemeCount` records (slice 2a, "How many
  // sounds do you hear in <word>?", multiple-choice over 4 numeral tiles)
  // are a distinct, independent-practice-safe item type living in
  // `foundationItems.json` — ADR-055's addendum (this slice) says so
  // explicitly. Served SCAFFOLDED via FoundationSession (see
  // FOUNDATION_SKILL_IDS in skillMeta.ts); ms.read.05 stays OUT of
  // chapters.json (pre-chapter inventory, unchanged) — its only serve-time
  // reachability channel is ms.read.06's walkback substitution
  // (learningProgressions.json reversed-first-prerequisite, MA-306's
  // resolveServedSkillId guard in useEngineQuestions.ts) plus direct
  // parent/teacher Assign (SkillTree.tsx / assignableSkills.ts, which walk
  // every microSkills.json row, not just chapters.json).
  "ms.read.05": "foundationItems",
  // RC-4 (reading-connection dispatch §3): phono skills route to their own
  // packs (RC-1 bundled 45 rhyming + 30 syllable_counting items, all
  // launch_safe; RC-2 shipped the mappers; RC-3.5 added the audio gate).
  // ms.read.0a (rhyming) and ms.read.0b (syllable_counting) are both ch1
  // supportSkills in chapters.json. Both have domainForSkill →
  // phonics_decoding → would wrongly fall to controlledSentences which has
  // no items for them.
  "ms.read.0a": "rhyming",
  "ms.read.0b": "syllable_counting",
  // Math foundation skills whose 25–30 authored launch_safe items live in
  // foundationItems but (lacking an override) routed to mathExemplars, which
  // carries none for them — so they served ZERO and the tree hid Assign. Route
  // them to their real pack. (task #82; render-verified via the content-integrity gate.)
  "ms.math.00g": "foundationItems",
  "ms.math.02": "foundationItems",
  // MA-218: ms.math.04 (Number recognition) now has 15 launch_safe MC
  // glyph-recognition items in foundationItems (FI_NUM_04_031..045).
  "ms.math.04": "foundationItems",
  // MA-208 follow-up (task #82 sibling — ms.math.00e was missed in the
  // original 00g/02/04 sweep): "Trace numerals 0-5" has 30 launch_safe MC
  // numeral_identification/numeral_to_quantity_match items in
  // foundationItems, but no override meant it fell through domainForSkill
  // → math_number_sense → default mathExemplars, which has 0 items for it
  // — servedItemCountForSkill returned 0 and the tree hid Assign. This
  // fixes the MC-content servability half only; the separate
  // numeral-tracing modality (traceNumeral schema, chapters.json entry,
  // FOUNDATION_SKILL_IDS/audience decision) stays deferred.
  "ms.math.00e": "foundationItems",
  // Depth wave (2026-08-01): ms.math.00f (Basic shapes) has 25 launch_safe
  // items in foundationItems but routed to mathExemplars (0 items) via the
  // default domain fallthrough. It became RUNTIME-reachable when ms.math.00g
  // was routed into ch1 (00f is 00g's reversed-first-prereq walkback target,
  // learningProgressions.json), so the walkback substitution needs a real
  // pool — same class as the ms.math.00e entry above. Mirrored in
  // perSkillPoolValidator.ts per the RC-V sync lesson.
  "ms.math.00f": "foundationItems",
};

// MA-663 — merged-pool routing. A skill in PACK_MERGE serves the UNION of
// every listed pack's content for it, in the given order, instead of the
// single pack primaryPackForSkill would pick. This is DISJOINT from
// PACK_OVERRIDE (a skill is either single-pack-routed via PACK_OVERRIDE/the
// domain default, or multi-pack-routed via PACK_MERGE — never both;
// packMergeReachabilityGuard.mjs's lint enforces the disjointness).
//
// ms.math.03 ("Which group has more?", a K comparison skill) is the first
// entry: primaryPackForSkill falls through the domain default to
// mathExemplars (no PACK_OVERRIDE — the domain math_number_sense default is
// correct for its 55 text-form items), so the 60 foundationItems records
// tagged targetSkill: "ms.math.03" (the skill's PICTURED form — a 5-year-
// old's real comparison task) were routed around entirely and never served
// on any pull path. The product rule (MA-663): neither pool may go dead —
// the pictured foundation items AND the text mathExemplars items both serve.
// primaryPackForSkill (below) is UNCHANGED for ms.math.03 (and every other
// skill) — it still names mathExemplars as ms.math.03's single "authoritative"
// pack for consumers that reason about one pack per skill (contentFileForSkill
// evidence rows, etc). PACK_MERGE is consulted ONLY at the item-pull sites
// that union pools (useEngineQuestions.ts's pullItems via
// getMergedContentForMicroSkill) and by the consumers this item updated to
// be merge-aware (perSkillPoolValidator.ts, mixedCoverageFloorGuard.mjs,
// mixedServableOperandSource.mjs, preReaderAnswerabilityCensus.mjs) — see
// the MA-663 BACKLOG block (docs/build/BACKLOG.md) for the full consumer
// list. contentFileForSkill itself is UNCHANGED (still names ms.math.03's
// one "authoritative" pack, mathExemplars, for consumers that reason about
// a single pack per skill) — per-served-ITEM attempt attribution is a
// separate function, contentFileForItem (useEngineQuestions.ts), which
// reads the served item's own sourcePack when present and falls back to
// this function otherwise. See useEngineQuestions.ts's doc comment on both.
//
// MA-687 adds three more skills whose `foundationItems` records were
// authored, launch_safe and never served (the same gap, same fix):
//   ms.math.00b — the 20 FI_CNT_00b counting records, the skill's only
//                 counts 1-5 (its mathExemplars pool is 6-10 only);
//   ms.math.00d — the 30 FI_NUM_00b records, rewritten to MA-688's
//                 spoken-target shape (word prompt + quantity ten frame)
//                 before routing, so none is glyph-matchable and none names
//                 a drag/picture-tile interaction the screen does not draw;
//   ms.math.05  — the 80 FI_CD5/FI_EXP2 records, the skill's only concrete
//                 five-frame/counters part-whole model, drawn as a concrete
//                 `number_bond` mathVisual (contentToEngineItem.ts).
// MA-807 adds four more (the same gap, the same fix):
//   ms.math.00a — the 20 FI_CNT_00a counting records (counts 1-5), drawn as
//                 a ten frame of the count like the skill's mathExemplars;
//   ms.math.00c — the 30 FI_NUM_00a records: 001-015 rewritten to MA-688's
//                 spoken-target shape (they drew the answer numeral over
//                 digit tiles), 016-025 keep MA-702's numeral card over
//                 dot-group tiles, 026-030 lose their drag copy and take that
//                 same dot-group-tile shape;
//   ms.math.01  — the 30 FI_SUB subitizing records (dot patterns 1-5);
//   ms.math.07  — the 60 FI_ADD5_021-030 / FI_EXP2_051-100 two-group join
//                 records, drawn as `groups`; the prompts that named a drag
//                 or a "whole mat" the screen does not draw take one of the
//                 pool's own tap prompts.
// ms.math.06's 30 FI_CD10 records stay dark (baseline rule: MA-694).
// Every launch_safe foundationItems/mathExemplars record is now either
// reachable through pullItems for its own skill or named, by rule and with a
// reason, in scripts/audit/poolReachabilityBaseline.json
// (scripts/audit/poolReachabilityGuard.mjs).
export const PACK_MERGE: Record<string, readonly ContentPackName[]> = {
  "ms.math.03": ["foundationItems", "mathExemplars"],
  "ms.math.00b": ["foundationItems", "mathExemplars"],
  "ms.math.00d": ["foundationItems", "mathExemplars"],
  "ms.math.05": ["foundationItems", "mathExemplars"],
  "ms.math.00a": ["foundationItems", "mathExemplars"],
  "ms.math.00c": ["foundationItems", "mathExemplars"],
  "ms.math.01": ["foundationItems", "mathExemplars"],
  "ms.math.07": ["foundationItems", "mathExemplars"],
};

export function primaryPackForSkill(skillId: string): ContentPackName {
  if (PACK_OVERRIDE[skillId]) return PACK_OVERRIDE[skillId];
  const domain = domainForSkill(skillId);
  switch (domain) {
    case "math_operations":
    case "math_number_sense":
    case "math_word_problems":
    case "math_geometry_measurement":
      return "mathExemplars";
    case "phonics_decoding":
    case "high_frequency_words":
      return "controlledSentences";
    case "decodable_connected_text":
      return "decodablePassages";
    // Wave A (roadmap 2026-06-15): the dark-but-built packs. These domains
    // had NO case and silently fell through to `mathExemplars` — so a
    // vocabulary skill (ms.read.16, a real recommended supportSkill) served
    // MATH items. Their mappers (S5.3) emit multiple_choice/tap_choice and
    // their per-pack distractor schemas are already hardened, so routing is
    // the only missing piece. semanticRelations/grammarItems/comprehension
    // carry no `targetSkill`, so getContentForMicroSkill returns the whole
    // launch_safe pool for whichever skill in the channel routes here.
    case "vocabulary_semantics":
      return "semanticRelations";
    case "grammar_sentence_language":
      return "grammarItems";
    case "reading_comprehension":
      return "comprehensionQuestions";
    // MA-128: handwritingMastery → spelling_encoding had NO case, silently
    // falling to the `default: mathExemplars` below — ms.read.dolch_trace
    // (Dolch sight-word tracing) routed to a math pack with zero matching
    // items. encodingWords is where sight-word/CVC content actually lives
    // (see the ms.read.07/ms.read.08 overrides above). Behavior-neutral
    // today: encodingWords is 100% targetSkill-tagged with none tagged for
    // this skill, so the served pool stays 0 either way (matches MA-133's
    // documented no-Assign chip) — this fixes the ROUTING, not the pool.
    case "spelling_encoding":
      return "encodingWords";
    default:
      return "mathExemplars";
  }
}
